/**
 * ``useAgent`` — the ONE bridge from the Agent pane UI into the SSE port
 * (F-AGENT PR-B). Orchestration glue ONLY (W1): it opens the stream, reduces
 * each NARROWED ``AgentEvent`` into ``useAgentStore`` via the store actions,
 * and owns abort lifecycle. It computes NOTHING legal — the BE owns every
 * reasoning step. Status node-id → label is a PRESENTATION lookup
 * (``t('agent.status.<node>')`` with a neutral fallback), never a verdict.
 *
 * Mirrors the FE ``use-agent-chat`` orchestration, stripped to the add-in's
 * event set (status | chunk | citations | registry | confidence | done |
 * error | abstention). No IRAC stepper, no sub-agent trace, no $/token (W6).
 *
 * Guards (plan §Q2/Q3):
 *   - Double-send: a new ``send`` while ``isStreaming`` aborts the in-flight
 *     controller first, then starts fresh.
 *   - Case-switch: when ``activeContext.caseId`` changes, the in-flight stream
 *     is aborted and the conversation is reset (the conversation is
 *     case-scoped — chaining into a thread tied to another case is wrong).
 *   - Unmount: the effect cleanup aborts; every post-await store write is
 *     guarded by ``if (signal.aborted) return`` so an unmounted write is
 *     harmless (state lives in the store, not the component).
 */

import { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { getContainer } from '@/container'
import { useAgentStore } from '@/ui/stores/use-agent-store'
import { useCaseStore } from '@/ui/stores/use-case-store'
import type { ChatMessage } from '@/domain/agent/entity'

export interface UseAgentResult {
  messages: ChatMessage[]
  isStreaming: boolean
  /** Presentation status label for the current pipeline node (W1). */
  statusText: string
  /** Send a turn. Creates the user + streaming-agent messages, opens the
   *  stream, reduces events into the store. No-op on an empty message. */
  send: (message: string) => void
  /** Abort the in-flight stream (the lawyer cancels / re-sends). */
  cancel: () => void
}

/** Mint a client-side message id. The BE does not key turns on the stream. */
function mintId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

/** ISO 8601 UTC timestamp ("Z" suffix) for a fresh turn. */
function nowIso(): string {
  return new Date().toISOString()
}

export function useAgent(): UseAgentResult {
  const { t, i18n } = useTranslation()

  // Narrow selectors (selector discipline) — never the whole store object.
  const messages = useAgentStore((s) => s.messages)
  const isStreaming = useAgentStore((s) => s.isStreaming)
  const statusText = useAgentStore((s) => s.statusText)

  // The active case anchor — re-read live; a change aborts + resets (below).
  const activeCaseId = useCaseStore((s) => s.activeContext?.caseId ?? null)

  const abortRef = useRef<AbortController | null>(null)

  /** Abort whatever stream is in flight (idempotent). */
  const cancel = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
  }, [])

  // ── Case-switch guard ──────────────────────────────────────────────────
  // When the active case changes, abort the in-flight stream and reset the
  // conversation: the chat is case-scoped (plan §Q3). We skip the very first
  // run (mount) so opening the pane on a bound doc doesn't wipe a hydrated
  // thread — the ref tracks the last-seen case id.
  const lastCaseRef = useRef<string | null>(activeCaseId)
  useEffect(() => {
    if (lastCaseRef.current === activeCaseId) return
    lastCaseRef.current = activeCaseId
    abortRef.current?.abort()
    abortRef.current = null
    useAgentStore.getState().reset()
  }, [activeCaseId])

  // ── Unmount guard ──────────────────────────────────────────────────────
  // Cleanup aborts the socket so a partial stream doesn't leak; the store is
  // the source of truth, so the post-await writes (guarded below) are safe.
  useEffect(() => {
    return () => {
      abortRef.current?.abort()
      abortRef.current = null
    }
  }, [])

  const send = useCallback(
    (raw: string) => {
      const message = raw.trim()
      if (message.length === 0) return

      // Double-send guard (plan §Q3): a fresh send while streaming aborts the
      // in-flight controller first.
      abortRef.current?.abort()

      const store = useAgentStore.getState()
      const active = useCaseStore.getState().activeContext
      const language = i18n.language

      // Seed the user turn + an empty streaming agent turn (the chunk
      // handler accretes into the latter via patchStreaming).
      store.addMessage({
        id: mintId('user'),
        role: 'user',
        text: message,
        timestamp: nowIso(),
        chips: [],
        status: 'final',
      })
      store.addMessage({
        id: mintId('agent'),
        role: 'agent',
        text: '',
        timestamp: nowIso(),
        chips: [],
        status: 'streaming',
      })
      store.setStreaming(true)
      store.setStatus(t('agent.status.connecting'))

      const controller = new AbortController()
      abortRef.current = controller
      const { signal } = controller

      void (async () => {
        try {
          const stream = getContainer().agentStream.stream(
            {
              message,
              caseId: active?.caseId,
              conversationId: store.conversationId ?? undefined,
              language,
            },
            { signal },
          )

          for await (const ev of stream) {
            if (signal.aborted) return
            reduceEvent(ev, t)
          }
        } catch (err) {
          if (signal.aborted) return
          if (err instanceof Error && err.name === 'AbortError') return
          // Network / non-OK response → mark the streaming turn as error.
          const s = useAgentStore.getState()
          s.finalize('error')
          s.setStreaming(false)
          s.setStatus('')
        } finally {
          if (abortRef.current === controller) abortRef.current = null
        }
      })()
    },
    [t, i18n],
  )

  return { messages, isStreaming, statusText, send, cancel }
}

/**
 * Reduce one NARROWED ``AgentEvent`` into the store. Pure presentation
 * routing (W1) — no legal math. Kept module-level so the ``send`` closure
 * stays small; reads the live store via ``getState`` each call.
 *
 * `t` is threaded for the status-label lookup ONLY (W1 presentation lookup).
 */
function reduceEvent(
  ev: import('@/domain/agent/entity').AgentEvent,
  t: (key: string, opts?: Record<string, unknown>) => string,
): void {
  const store = useAgentStore.getState()
  switch (ev.type) {
    case 'status': {
      // Presentation lookup: known node → its label; unknown node → the
      // neutral fallback (never a raw node id, never an invented verdict).
      const label = t(`agent.status.${ev.data}`, {
        defaultValue: t('agent.status.fallback'),
      })
      store.setStatus(label)
      break
    }
    case 'chunk':
      store.patchStreaming(ev.data)
      break
    case 'citations':
      // Citations carry resolved slices; the chip descriptors land via the
      // `registry` event (W7 — chips render from the BE-built descriptor).
      // No store action consumes raw citations today; the registry event is
      // the chip source of truth, so this is a no-op pass-through.
      break
    case 'registry':
      attachChips(ev.chips)
      break
    case 'confidence':
      // Stored confidence drives no UI math here (W6/W1) — the add-in shows
      // no numeric score. Intentionally inert; the BE owns abstention gating.
      break
    case 'done':
      store.finalize('final')
      store.setStreaming(false)
      store.setStatus('')
      break
    case 'abstention': {
      // Abstention is a terminal low-confidence state, distinct from error.
      // The streamed message text (the BE-supplied abstention message, if it
      // streamed) is finalized as `abstention`; no chips invented.
      //
      // Firewall-block parity (plan PR-F): the citation-firewall abstention
      // ships `{reason, hallucinated}` with NO `message` and a blanked
      // response. Localize the copy from `reason` (W1 presentation lookup, W8
      // bundled i18n) and REPLACE the streaming body with it.
      //
      // We replace (not "only-if-empty"): PR-E's sentence-gated reveal can
      // stream a verified answer PREFIX live before the firewall blocks a
      // later sentence. Keeping that prefix would show a partial answer
      // labelled "abstention" with the firewall explanation suppressed (the
      // adversarial-review A2 finding). The withheld answer must never stand
      // in for the abstention — swap the whole body for the firewall copy.
      // Unknown reasons (no bundled copy) keep whatever the BE streamed.
      const copy = firewallAbstentionCopy(t, ev.reason)
      if (copy) store.replaceStreamingText(copy)
      store.finalize('abstention')
      store.setStreaming(false)
      store.setStatus('')
      break
    }
    case 'error':
      store.finalize('error')
      store.setStreaming(false)
      store.setStatus('')
      break
  }
}

/** Localized body for a known abstention `reason` when the BE streamed no
 *  message (plan PR-F). Mirrors the web `AbstentionBlock` what/why/remedy
 *  copy for `citation_firewall_blocked`. Returns null for an unknown reason
 *  so the bubble keeps its neutral abstention label without inventing copy.
 *  W1 — a fixed reason→copy map is presentation, not a legal verdict. */
function firewallAbstentionCopy(
  t: (key: string, opts?: Record<string, unknown>) => string,
  reason: string | undefined,
): string | null {
  if (reason !== 'citation_firewall_blocked') return null
  return [
    t('agent.firewallBlocked.what'),
    t('agent.firewallBlocked.why'),
    `→ ${t('agent.firewallBlocked.remedy')}`,
  ].join('\n\n')
}

/** Attach the BE-built citation chips to the CURRENT streaming agent turn
 *  (W7 — chips come only from the `registry` payload, never invented). No-op
 *  when the last turn is not a streaming agent message. */
function attachChips(
  chips: import('@/domain/agent/entity').AgentCitationChip[],
): void {
  if (chips.length === 0) return
  useAgentStore.setState((state) => {
    const last = state.messages[state.messages.length - 1]
    if (!last || last.role !== 'agent' || last.status !== 'streaming') {
      return state
    }
    const patched: ChatMessage = { ...last, chips }
    return { messages: [...state.messages.slice(0, -1), patched] }
  })
}
